import { Browser } from '@capacitor/browser'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom-original'
import { useTheme } from 'styled-components'

import { FlexColumn, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3, H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { mixpanel } from '@/mixpanel'
import { openUrl } from '@/utils/external-resources'
import { PageLink } from '../../components/page-link/PageLink'
import { StList } from './ProfileMandates.styled'

const ProfileMandatesPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()

  return (
    <ContentPageLayout
      noScroll
      header={
        <AppHeader
          primaryAction={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              navigate('/profile', { replace: true })
            },
          }}
        />
      }
    >
      <Padding pt={theme.UI.Spacing.Space4} pb={theme.UI.Spacing.Space8}>
        <FlexColumn gap={theme.UI.Spacing.Space8}>
          <H3 noMargin>
            {t({
              id: 'profile.mandates-permissions.title',
              comment: 'Mandates or permissions',
              message: 'Mandaten of volmachten',
            })}
          </H3>
          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <PageLink
              icon={['fal', 'external-link']}
              external
              onClick={() => {
                mixpanel.track('mandate_management_CSAM_button_clicked')

                openUrl(
                  t({
                    id: 'globals.mandate-management.url',
                    comment: 'Link to mandate management',
                    message: 'https://www.csam.be/nl/beheer-mandaten.html',
                  }),
                )
              }}
            >
              {t({
                id: 'profile.mandates-permissions.manage-mandates',
                comment: 'Manage mandates',
                message: 'Mandaten beheren (CSAM)',
              })}
            </PageLink>
            <Line />
          </FlexColumn>
          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <H4 noMargin>
              {t({
                id: 'profile.mandates-permissions.what-are-mandates',
                comment: 'What are mandates?',
                message: 'Wat zijn mandaten?',
              })}
            </H4>
            <Span type='BodyLargeRegular'>
              {t({
                id: 'profile.mandates-permissions.what-are-mandates.description',
                comment: 'What are mandates description?',
                message:
                  'Met een mandaat geef je iemand toegang tot je medische gegevens en afspraken. Mandaten ken je niet toe in deze app, maar via het officiële portaal CSAM.',
              })}
            </Span>
          </FlexColumn>
          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <H4 noMargin>
              {t({
                id: 'profile.mandates-permissions.who-are-mandates-needed-for',
                comment: 'Who are mandates needed for?',
                message: 'Voor wie zijn mandaten nodig?',
              })}
            </H4>
            <StList>
              <li>
                <Span type='BodyLargeRegular'>
                  <Span type='BodyLargeBold'>Kinderen &lt; 16 jaar:</Span> je ouders of voogd beheren de toegang.
                </Span>
              </li>
              <li>
                <Span type='BodyLargeRegular'>
                  <Span type='BodyLargeBold'>Vanaf 16 jaar:</Span> je geeft zelf toestemming via CSAM. Je ouders hebben
                  geen automatisch recht meer.
                </Span>
              </li>
              <li>
                <Span type='BodyLargeRegular'>
                  <Span type='BodyLargeBold'>Volwassenen:</Span> je kan een vertrouwenspersoon toegang geven.
                </Span>
              </li>
            </StList>
          </FlexColumn>
          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <H4 noMargin>
              {t({
                id: 'profile.mandates-permissions.how-to-register-mandates',
                comment: 'How to register mandates?',
                message: 'Hoe een mandaat registreren?',
              })}
            </H4>
            <Span type='BodyLargeRegular'>
              Wil je meer uitleg over het aanmaken en beheren van mandaten? Bekijk dan onderstaande pagina van CoZo:
            </Span>
            <PageLink
              icon={['fal', 'external-link']}
              onClick={async () => {
                mixpanel.track('mandate_registration_cozo_button_clicked')

                // In app, this always needs to be opened in an app browser
                // On web, this works just like opening a new tab
                await Browser.open({
                  url: t({
                    id: 'globals.mandate-register.url',
                    comment: 'Link to register mandates',
                    message: 'https://www.cozo.be/mandaat_hoeregistreren',
                  }),
                })
              }}
            >
              {t({
                id: 'profile.mandates-permissions.how-to-register-mandates',
                comment: 'how to register mandates',
                message: 'Hoe een mandaat registreren?',
              })}
            </PageLink>
            <Line />
          </FlexColumn>
          <FlexColumn gap={theme.UI.Spacing.Space4}>
            <H4 noMargin>
              {t({
                id: 'profile.mandates-permissions.attention-points',
                comment: 'Attention points',
                message: 'Belangrijke aandachtspunten',
              })}
            </H4>
            <StList>
              <li>
                <Span type='BodyLargeRegular'>
                  Het aanmaken van een mandaat kan tot 24 uur duren. Tot dan kan je de app niet via een mandaat
                  gebruiken.
                </Span>
              </li>
              <li>
                <Span type='BodyLargeRegular'>
                  Heb je technische problemen? Neem contact op met de klantendienst van CSAM.
                </Span>
              </li>
            </StList>
          </FlexColumn>
        </FlexColumn>
      </Padding>
    </ContentPageLayout>
  )
}

export { ProfileMandatesPage }
